import classNames from 'classnames';
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useValueRef } from '../../hooks';
import styles from './CrossFade.module.css';
async function tick({
  frames
}: {
  frames: number[];
}) {
  return new Promise(resolve => frames.push(window.requestAnimationFrame(resolve)));
}
async function wait(noOfTicks: number, frames: number[]) {
  const promiseFn = new Array(noOfTicks).fill(0).map((_, __) => () => tick({
    frames
  }));
  for (const promise of promiseFn) {
    await promise();
  }
}
export type AcceptableChildren = string | number | boolean | null | undefined;
export type CrossFadeProps = {
  children?: AcceptableChildren;
  className?: string | undefined;
};

/** TODO: Re-write this component to actually work with react-hooks/refs */
/* eslint-disable react-hooks/refs */
function CrossFade({
  children,
  className
}: CrossFadeProps) {
  const prevChildrenRef = useRef<ReactNode>(null);
  const childrenRef = useRef<ReactNode>(null);
  const [index, setIndex] = useState<0 | 1>(0);
  const indexRef = useValueRef<0 | 1>(index);
  const ref0 = useRef<HTMLDivElement>(null);
  const ref1 = useRef<HTMLDivElement>(null);
  const refs = useRef([ref0, ref1] as const);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  const handleChildrenChange = useCallback(async (children_0: AcceptableChildren, frames: number[]) => {
    if (children_0 === prevChildrenRef.current) return;
    prevChildrenRef.current = children_0;
    childrenRef.current = children_0;
    const i = indexRef.current;
    const j = i === 0 ? 1 : 0;
    setIndex(j);
    await wait(4, frames);
    containerRef.current!.style.maxWidth = `${refs.current[j].current!.clientWidth}px`;
    containerRef.current!.style.minWidth = `${refs.current[j].current!.clientWidth}px`;
  }, [indexRef]);
  useEffect(() => {
    setMounted(true);
    const frames_0: number[] = [];
    void handleChildrenChange(children, frames_0).then().catch();
    return () => {
      frames_0.forEach(frame => cancelAnimationFrame(frame));
      prevChildrenRef.current = children;
    };
  }, [children, handleChildrenChange]);
  return <div className={styles.container} ref={containerRef}>
      <div className={classNames(styles.crossFade, className, {
      [styles.hidden!]: index !== 0
    })} ref={ref0} />
      <div className={classNames(styles.crossFade, className, {
      [styles.hidden!]: index !== 1
    })} ref={ref1} />
      {mounted && createPortal(childrenRef.current ?? null, refs.current[index].current!)}
      {mounted && createPortal(prevChildrenRef.current ?? null, refs.current[index === 0 ? 1 : 0].current!)}
    </div>;
}
export default memo(CrossFade);